import { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";

interface Reaction {
  id: number;
  x: number;
  y: number;
  emoji: string;
  scale: number;
}

interface EmojiReactionsProps {
  enabled?: boolean;
  intensity: "subtle" | "normal" | "chaos";
}

const EmojiReactions = ({ enabled = true, intensity }: EmojiReactionsProps) => {
  const [reactions, setReactions] = useState<Reaction[]>([]);

  const reactionEmojis = {
    subtle: ["❤️", "💕", "💗"],
    normal: ["❤️", "💕", "💗", "💖", "✨", "💫", "🌟"],
    chaos: ["❤️", "💕", "💗", "💖", "✨", "💫", "🌟", "💋", "🌹", "🥀", "💝", "💘", "🎀", "🦋", "🌸"],
  };

  const emojis = reactionEmojis[intensity];
  
  const spawnCount = {
    subtle: 3,
    normal: 6,
    chaos: 12,
  };

  const handleClick = useCallback((e: React.MouseEvent) => {
    if (!enabled) return;

    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const newReactions: Reaction[] = Array.from({ length: spawnCount[intensity] }, (_, i) => ({
      id: Date.now() + i,
      x: x + (Math.random() - 0.5) * 60,
      y: y + (Math.random() - 0.5) * 60,
      emoji: emojis[Math.floor(Math.random() * emojis.length)],
      scale: 0.8 + Math.random() * 0.8,
    }));

    setReactions(prev => [...prev, ...newReactions]);

    // Clean up after animation
    setTimeout(() => {
      setReactions(prev => prev.filter(r => !newReactions.find(nr => nr.id === r.id)));
    }, 2000);
  }, [enabled, intensity, emojis]);

  return (
    <div 
      className="fixed inset-0 z-30 cursor-pointer"
      onClick={handleClick}
    >
      <AnimatePresence>
        {reactions.map((reaction) => (
          <motion.div
            key={reaction.id}
            className="absolute pointer-events-none select-none"
            style={{
              left: reaction.x,
              top: reaction.y,
              fontSize: 24 * reaction.scale,
            }}
            initial={{ 
              scale: 0, 
              opacity: 1,
              y: 0,
            }}
            animate={{ 
              scale: [0, 1.2, 1],
              opacity: [1, 1, 0],
              y: -100 - Math.random() * 50,
              x: (Math.random() - 0.5) * 80,
              rotate: (Math.random() - 0.5) * 60,
            }}
            exit={{ opacity: 0 }}
            transition={{ 
              duration: 1.5 + Math.random() * 0.5,
              ease: "easeOut",
            }}
          >
            {reaction.emoji}
          </motion.div>
        ))}
      </AnimatePresence>

      {/* Hint text */}
      {enabled && (
        <motion.p
          className="fixed bottom-20 left-1/2 -translate-x-1/2 text-pink-400/30 text-sm pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 3 }}
        >
          ✨ Click anywhere for magic ✨
        </motion.p>
      )}
    </div>
  );
};

export default EmojiReactions;
